import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../services/apiClient';
import { motion } from 'motion/react';
import { Cpu, HardDrive, Wifi, Activity, Server, Database, ArrowLeftRight } from 'lucide-react';

interface DiskStat {
  mount: string;
  used_gb: number;
  total_gb: number;
  free_gb: number;
  percent: number;
}

interface DockerContainer {
  name: string;
  image: string;
  cpu_percent: string;
  mem_usage: string;
  uptime: string;
}

interface SystemStats {
  cpu: number;
  ram: { used: number; total: number; percent: number };
  disk: DiskStat[];
  loadavg: number[];
  net: { rx_bytes: number; tx_bytes: number };
  swap: { used_gb: number; total_gb: number; percent: number };
  docker?: { containers: DockerContainer[]; count: number };
  platform: string;
  release: string;
  arch: string;
  hostname: string;
  cpus: number;
  uptime: number;
}

export function KernelMonitorApp({ t }: { t: any }) {
  const [data, setData] = useState<number[]>([]);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const prevNetRef = useRef<{ rx: number; tx: number; ts: number } | null>(null);
  const [netSpeed, setNetSpeed] = useState({ rx: 0, tx: 0 });

  useEffect(() => {
    let unmounted = false;
    const fetchStats = async (controller: AbortController) => {
      try {
        const res = await apiFetch('/api/system/stats', { signal: controller.signal });
        if (!res.ok) {
          if (!unmounted) setError(`HTTP ${res.status}`);
          return;
        }
        const sys = await res.json();
        if (!unmounted) {
          setStats(sys);
          setError(null);
          setData(prev => {
            const next = [...prev, Number(sys.cpu) || 0];
            return next.slice(-30);
          });
        }
        // 网络流量速率计算（delta）
        if (sys.net) {
          const now = Date.now();
          if (prevNetRef.current) {
            const dt = (now - prevNetRef.current.ts) / 1000;
            if (dt > 0 && dt < 60) {
              const rxSpeed = (sys.net.rx_bytes - prevNetRef.current.rx) / dt / 1024 / 1024;
              const txSpeed = (sys.net.tx_bytes - prevNetRef.current.tx) / dt / 1024 / 1024;
              if (!unmounted) {
                setNetSpeed({
                  rx: isFinite(rxSpeed) ? Math.max(0, rxSpeed) : 0,
                  tx: isFinite(txSpeed) ? Math.max(0, txSpeed) : 0,
                });
              }
            }
          }
          prevNetRef.current = { rx: sys.net.rx_bytes, tx: sys.net.tx_bytes, ts: now };
        }
      } catch (err: unknown) {
        if (!unmounted && (err as Error)?.name !== 'AbortError') {
          if (!stats) setError(t.kernelConnectionFailed || '连接失败');
        }
      }
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    fetchStats(controller);

    const interval = setInterval(() => {
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 5000);
      fetchStats(ctrl).finally(() => clearTimeout(tid));
    }, 2000);

    return () => {
      unmounted = true;
      clearInterval(interval);
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  if (!stats) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          {error ? (
            <>
              <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                <Activity size={18} className="text-red-400" />
              </div>
              <span className="text-xs text-red-300/70 font-mono">{error}</span>
              <span className="text-[10px] text-white/20 font-mono uppercase">{t.kernelRetrying || '重试中...'}</span>
            </>
          ) : (
            <>
              <div className="w-8 h-8 border-2 border-celestial-saturn/30 border-t-celestial-saturn rounded-full animate-spin" />
              <span className="text-xs text-white/30 font-mono uppercase tracking-widest">{t.kernelLoading || '连接服务器...'}</span>
            </>
          )}
        </div>
      </div>
    );
  }

  const chipLabel = stats.platform ? `${stats.platform.toUpperCase()}_${stats.arch.toUpperCase()}_NODE` : 'NEURAL_NODE';
  const uptimeFmt = stats.uptime ? `${Math.floor(stats.uptime / 3600)}h ${Math.floor((stats.uptime % 3600) / 60)}m ${Math.floor(stats.uptime % 60)}s` : '';
  const loadStatus = stats.cpu > 80 ? 'WARN' : stats.cpu > 50 ? 'LOAD' : 'IDLE';
  const primaryDisk = stats.disk?.find(d => d.mount === '/') || stats.disk?.[0];
  const loadColor = stats.cpu > 80 ? 'text-red-400' : stats.cpu > 50 ? 'text-celestial-saturn' : 'text-emerald-400';

  return (
    <div className="h-full overflow-y-auto custom-scrollbar p-5 space-y-4 font-sans">
      {/* ── 顶部服务器信息条 ── */}
      <div className="flex justify-between items-center bg-black/40 p-5 rounded-[2rem] border border-white/5 backdrop-blur-xl">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border shadow-[0_0_20px_rgba(255,200,80,0.1)] transition-colors duration-700 ${
            stats.cpu > 80 ? 'bg-red-500/15 text-red-400 border-red-500/25' : 'bg-celestial-saturn/10 text-celestial-saturn border-celestial-saturn/20'
          }`}>
            <Server size={24} />
          </div>
          <div>
            <div className="text-xs font-black text-white/40 uppercase tracking-widest leading-none mb-1">
              {t.kernelServerIp || '81.70.11.192'} · {stats.hostname || t.localIntelNode || '节点'}
            </div>
            <div className="text-lg font-black text-white tracking-tight">{chipLabel}</div>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-xs font-black uppercase tracking-widest leading-none mb-1 ${loadColor}`}>
            {loadStatus} · {stats.cpus}核 · {uptimeFmt}
          </div>
          <div className="text-xs font-mono text-white/40">{stats.release || ''}</div>
        </div>
      </div>

      {/* ── 6 指标卡片 ── */}
      <div className="grid grid-cols-3 gap-3">
        <MetricCard
          icon={<Cpu size={14} />}
          label={t.neuralThroughput || 'CPU负载'}
          value={`${stats.cpu}%`}
          bar={stats.cpu}
          color="bg-celestial-saturn"
          sub={`1m ${Number(stats.loadavg?.[0])?.toFixed(1) || '-'} · 5m ${Number(stats.loadavg?.[1])?.toFixed(1) || '-'} · 15m ${Number(stats.loadavg?.[2])?.toFixed(1) || '-'}`}
          alert={stats.cpu > 80}
        />
        <MetricCard
          icon={<Database size={14} />}
          label={t.synapticLoad || '内存'}
          value={`${stats.ram.used} / ${stats.ram.total} GB`}
          bar={stats.ram.percent}
          color="bg-emerald-500"
          sub={`已用 ${stats.ram.percent}% · 可用 ${Number(stats.ram.total - stats.ram.used).toFixed(1)} GB`}
          alert={stats.ram.percent > 85}
        />
        <MetricCard
          icon={<HardDrive size={14} />}
          label={t.kernelDiskUsage || '磁盘'}
          value={primaryDisk ? `${primaryDisk.used_gb} / ${primaryDisk.total_gb} GB` : '-'}
          bar={primaryDisk?.percent ?? 0}
          color="bg-blue-500"
          sub={primaryDisk ? `${primaryDisk.mount} · 剩余 ${primaryDisk.free_gb}GB` : ''}
          alert={(primaryDisk?.percent ?? 0) > 80}
        />
        <MetricCard
          icon={<Wifi size={14} />}
          label={t.kernelNetworkTraffic || '网络流量'}
          value={`↓${netSpeed.rx.toFixed(1)} ↑${netSpeed.tx.toFixed(1)} MB/s`}
          color="bg-violet-500"
          sub={(() => {
            const rxGB = (stats?.net?.rx_bytes || 0) / 1073741824;
            const txGB = (stats?.net?.tx_bytes || 0) / 1073741824;
            if (rxGB >= 1000 || txGB >= 1000) {
              return `总 ↓${(rxGB / 1024).toFixed(1)} ↑${(txGB / 1024).toFixed(1)} TB`;
            }
            return `总 ↓${rxGB.toFixed(1)} ↑${txGB.toFixed(1)} GB`;
          })()}
          noBar
        />
        <MetricCard
          icon={<ArrowLeftRight size={14} />}
          label={t.kernelSwapUsage || '交换分区'}
          value={`${stats.swap?.used_gb ?? 0} / ${stats.swap?.total_gb ?? 0} GB`}
          bar={stats.swap?.percent ?? 0}
          color="bg-orange-500"
          sub={`使用率 ${stats.swap?.percent ?? 0}%`}
          alert={(stats.swap?.percent ?? 0) > 50}
        />
        <MetricCard
          icon={<Activity size={14} />}
          label={t.kernelSystemLoad || '系统信息'}
          value={`${stats.cpus}核 · ${stats.arch?.toUpperCase() || ''}`}
          color="bg-cyan-500"
          sub={`${stats.platform?.toUpperCase() || ''} · ${stats.release?.split('.')[0] || ''}`}
          noBar
        />
      </div>

      {/* ── CPU 历史柱状图 ── */}
      <div className="h-40 bg-black/40 rounded-[2rem] border border-white/5 p-5 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
        </div>
        <div className="absolute top-4 left-5 text-[10px] font-black text-white/25 uppercase tracking-widest flex items-center gap-1.5">
          <Activity size={10} />
          {t.kernelCpuHistory || 'CPU 30秒'} · max {Math.max(...data, 0)}%
        </div>
        <div className="relative h-full flex items-end gap-0.5 pt-6">
          {data.length === 0 ? (
            <div className="absolute inset-0 flex items-center justify-center text-white/15 text-xs font-mono">— {t.kernelNoData || '采集数据中'} —</div>
          ) : (
            data.map((val, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${Math.max(val, 1)}%` }}
                transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                className={`flex-1 rounded-t-sm ${val > 80 ? 'bg-gradient-to-t from-red-500/60 to-red-400' : val > 50 ? 'bg-gradient-to-t from-celestial-saturn/40 to-celestial-saturn' : 'bg-gradient-to-t from-emerald-500/30 to-emerald-400'}`}
                style={{ minWidth: '4px' }}
              />
            ))
          )}
        </div>
      </div>

      {/* ── 磁盘挂载详情 ── */}
      {stats.disk && stats.disk.length > 0 && (
        <div className="rounded-[2rem] border border-white/5 bg-black/20 p-5">
          <div className="mb-3 flex items-center gap-2">
            <HardDrive size={14} className="text-blue-400" />
            <h3 className="text-xs font-black uppercase tracking-widest text-white/50">{t.kernelMountPoint || '磁盘挂载'}</h3>
          </div>
          <div className="space-y-1.5">
            {stats.disk.map((d, i) => (
              <div key={i} className="group flex items-center justify-between py-2 px-4 bg-white/[0.02] rounded-2xl hover:bg-white/[0.06] transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-white/50 w-28">{d.mount}</span>
                  <span className="text-xs font-mono text-white/70">
                    {d.used_gb}GB<span className="text-white/25"> / {d.total_gb}GB</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${d.percent}%` }}
                      className={`h-full rounded-full ${d.percent > 85 ? 'bg-red-500' : d.percent > 60 ? 'bg-orange-500' : 'bg-blue-500'}`}
                    />
                  </div>
                  <span className={`text-xs font-bold w-10 text-right ${d.percent > 85 ? 'text-red-400' : 'text-white/50'}`}>
                    {d.percent}%
                  </span>
                  <span className="text-[10px] font-mono text-white/25 opacity-0 group-hover:opacity-100 transition-opacity">
                    {t.kernelDiskFree || '空闲'} {d.free_gb}GB
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Docker 容器面板 ── */}
      {stats.docker && stats.docker.containers && stats.docker.containers.length > 0 && (
        <div className="rounded-[2rem] border border-white/5 bg-black/20 p-5">
          <div className="mb-3 flex items-center gap-2">
            <Server size={14} className="text-cyan-400" />
            <h3 className="text-xs font-black uppercase tracking-widest text-white/50">
              {t.kernelDockerContainers || 'Docker 容器'} · {stats.docker.count} {t.kernelRunning || '运行中'}
            </h3>
          </div>
          <div className="space-y-1.5">
            {stats.docker.containers.map((c, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-4 bg-white/[0.02] rounded-2xl hover:bg-white/[0.06] transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]" />
                  <span className="text-xs font-bold text-white/70">{c.name}</span>
                  <span className="text-[10px] font-mono text-white/25">{c.image}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-[10px] font-mono text-celestial-saturn/80">{c.cpu_percent}</span>
                  <span className="text-[10px] font-mono text-emerald-400/80">{c.mem_usage}</span>
                  <span className="text-[10px] font-mono text-white/30">{c.uptime}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════ MetricCard 子组件 ══════ */
function MetricCard({
  icon, label, value, bar = 0, color, sub, noBar, alert,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  bar?: number;
  color: string;
  sub?: string;
  noBar?: boolean;
  alert?: boolean;
}) {
  return (
    <div className={`group p-4 rounded-[1.5rem] border transition-all duration-300 cursor-default ${
      alert ? 'bg-red-500/5 border-red-500/15' : 'bg-white/5 border-white/5 hover:bg-white/10'
    }`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className={alert ? 'text-red-400' : 'text-white/30'}>{icon}</span>
        <span className={`text-[11px] font-black uppercase tracking-[0.15em] ${alert ? 'text-red-300/80' : 'text-white/40'}`}>{label}</span>
      </div>
      <div className={`text-base font-black tracking-tighter mb-1.5 ${alert ? 'text-red-300' : 'text-white'}`}>{value}</div>
      {!noBar && bar > 0 && (
        <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden mb-1.5">
          <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(bar, 100)}%` }} className={`h-full rounded-full ${color}`} />
        </div>
      )}
      {sub && <div className="text-[10px] font-mono text-white/25">{sub}</div>}
    </div>
  );
}
